import type { ColumnDef } from '@tanstack/react-table';

import {
    type GetTransactionsQuery,
    type TransactionsFilterInput,
    TransactionState,
    PaymentMethodProvider,
} from 'data/__generated__';
import { textColumn, multiSelectColumn, dateRangeColumn, numberRangeColumn, entityColumn } from 'components/DataTable';
import {
    anyStateMicrostoreColumnSource,
    anyStateProductSource,
    anyStateSiteSource,
    anyStateUserSource,
} from 'data/entity-sources/sources';
import { formatPrice } from 'utils/price';

import { StatusTag } from '../components/StatusTag';
import { TRANSACTIONS_STATES_LABELS } from './states';
import { DetailLink } from 'components/DetailsView/DetailLink';
import { TransactionPaymentMethodsCell } from 'pages/Payments/components/TransactionPaymentMethodsCell';

export type TransactionRow = GetTransactionsQuery['transactions']['transactions'][number];

export const transactionsColumns: ColumnDef<TransactionRow, unknown>[] = [
    dateRangeColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'createdAt',
        header: 'Date de création',
        filterKey: 'createdAt',
        defaultVisible: true,
    }),
    // Masquée par défaut : elle n'a d'intérêt que pour retrouver ce qui a bougé APRÈS la vente
    // (remboursement, reprise d'un paiement en attente, changement de carte).
    dateRangeColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'updatedAt',
        header: 'Date de modification',
        filterKey: 'updatedAt',
    }),
    textColumn<TransactionRow, TransactionsFilterInput>({
        id: 'transactionType',
        accessorFn: (row) => row.basket?.type ?? '-',
        header: 'Type',
        defaultVisible: true,
        cell: ({ row }) => {
            const { basket, basketId } = row.original;

            if (!basketId || !basket) {
                return '-';
            }

            return <DetailLink path="basket" name={basket.type} value={basketId} />;
        },
    }),
    multiSelectColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'newState',
        header: 'Statut',
        filterKey: 'state',
        defaultVisible: true,
        options: Object.values(TransactionState).map((state) => ({
            value: state,
            label: TRANSACTIONS_STATES_LABELS[state],
        })),
        renderOption: (option) => <StatusTag state={option.value as TransactionState} />,
        cell: ({ row }) => <StatusTag state={row.original.newState} />,
    }),
    multiSelectColumn<TransactionRow, TransactionsFilterInput>({
        id: 'provider',
        accessorFn: (row) => row.paymentMethods?.[0]?.provider ?? '-',
        header: 'Provider',
        filterKey: 'provider',
        defaultVisible: true,
        options: Object.values(PaymentMethodProvider).map((p) => ({ value: p, label: p })),
        cell: ({ row }) => row.original.paymentMethods?.[0]?.provider ?? '-',
    }),
    textColumn<TransactionRow, TransactionsFilterInput>({
        id: 'paymentMethods',
        accessorFn: (row) => row.paymentMethods?.map(({ paymentMethodName }) => paymentMethodName).join(', ') ?? '',
        header: 'Moyens de paiement',
        defaultVisible: true,
        cell: ({ row }) => <TransactionPaymentMethodsCell paymentMethods={row.original.paymentMethods} />,
    }),
    // Site, colonne, utilisateur et produit affichent un NOM et filtrent sur des IDENTIFIANTS : plus de
    // recherche textuelle approximative, et deux homonymes ne se confondent plus. Les sources portent la
    // recherche serveur et la résolution des libellés d'une sélection restaurée depuis l'URL.
    entityColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'siteName',
        header: 'Site',
        filterKey: 'siteIds',
        defaultVisible: true,
        source: anyStateSiteSource,
        cell: ({ row }) => (
            <DetailLink path="site" name={row.original.siteName} value={row.original.siteId ?? 'Pas de site'} />
        ),
    }),
    entityColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'microstoreColumnName',
        header: 'Colonne',
        filterKey: 'microstoreColumnIds',
        defaultVisible: true,
        source: anyStateMicrostoreColumnSource,
        cell: ({ row }) => (
            <DetailLink
                path="microstore-column"
                name={row.original.microstoreColumnName}
                value={row.original.microstoreColumnId ?? 'Pas de colonne'}
            />
        ),
    }),
    entityColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'userFullName',
        header: 'Utilisateur',
        filterKey: 'userIds',
        defaultVisible: true,
        source: anyStateUserSource,
    }),
    entityColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'productName',
        header: 'Produit',
        filterKey: 'productIds',
        defaultVisible: true,
        source: anyStateProductSource,
    }),
    numberRangeColumn<TransactionRow, TransactionsFilterInput>({
        accessorKey: 'priceHT',
        header: 'Prix HT',
        filterKey: 'priceHT',
        defaultVisible: true,
        cell: ({ row }) => formatPrice(row.original.priceHT),
    }),
    textColumn<TransactionRow, TransactionsFilterInput>({
        id: 'priceTTC',
        accessorFn: (row) => row.priceHT * (1 + row.tva),
        header: 'Montant TTC',
        defaultVisible: true,
        cell: ({ row }) => formatPrice(row.original.priceHT * (1 + row.original.tva)),
    }),
];
